'use client';
import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useToast } from './toast';
import { classifyError } from './errorMessage';
import { useErrorMessage } from './useErrorMessage';

const NO_RETRY = new Set(['forbidden', 'validation']);

/**
 * Toast for a failed write. `title` names the action ("Couldn't delete Open Category").
 * The message is the server's own sentence when it wrote one, else a translated
 * sentence for the failure kind. Retry is offered when retrying can help.
 */
export function useErrorToast() {
  const { showToast } = useToast();
  const t = useTranslations('Common');
  const messageFor = useErrorMessage();
  return useCallback(
    (err: unknown, opts: { title: string; retry?: () => void }) => {
      const kind = classifyError(err);
      const message = messageFor(err);
      const canRetry = opts.retry && !NO_RETRY.has(kind);
      showToast(message, 'error', {
        title: opts.title,
        ...(canRetry ? { action: { label: t('buttons.retry'), onClick: opts.retry! } } : {}),
      });
    },
    [showToast, t, messageFor],
  );
}
